import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowsClockwise, Download, Funnel, ChartLine, Warning, TrendUp, TrendDown } from "@phosphor-icons/react";
import api from "../lib/api";
import StrategyMatrixSection from "../components/StrategyMatrixSection";
import TodaysSlatePanel from "../components/TodaysSlatePanel";

function money(n) {
  if (n == null) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function pct(n) { return n == null ? "—" : `${(n * 100).toFixed(1)}%`; }

function Kpi({ label, value, accent, sub }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className={`font-display text-4xl leading-none mt-2 ${accent || "text-white"}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono-d text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function csvDownload(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function BettingLab() {
  const [bankroll, setBankroll] = useState(2000);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Active shortlist state for the new drill-down section (needed so the C-view
  // banner can resolve team_id → abbr labels). Fetched lazily on mount.
  const [activeTeams, setActiveTeams] = useState({ iso: [], wc: [], allTeams: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data: t } = await api.get("/admin/strategy/teams");
        setActiveTeams({
          iso: t?.active?.iso || [],
          wc: t?.active?.wc || [],
          allTeams: t?.all_teams || [],
        });
      } catch {/* non-blocking — banner will just show team IDs */}
    })();
  }, []);

  // strategy filter
  const [filterMode, setFilterMode] = useState("all"); // all | above_500 | custom | custom_wc
  const PRESET_CUSTOM = [134, 144, 119, 147]; // Pirates, Braves, Dodgers, Yankees
  const PRESET_WC = [119, 147, 144, 112, 139]; // Dodgers, Yankees, Braves, Cubs, Rays
  const [customTeams, setCustomTeams] = useState(PRESET_CUSTOM);
  const [wcTeams, setWcTeams] = useState(PRESET_WC);

  // comparison
  const [comparison, setComparison] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // filters
  const [teamFilter, setTeamFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [onlyBets, setOnlyBets] = useState(false);

  const buildParams = (mode = filterMode, teams = customTeams, wc = wcTeams) => {
    const p = { starting_bankroll: bankroll };
    if (mode === "custom_wc") {
      p.filter_mode = "custom";
      p.filter_teams = teams.join(",");
      p.wc_teams = wc.join(",");
    } else {
      p.filter_mode = mode;
      if (mode === "custom") p.filter_teams = teams.join(",");
    }
    return p;
  };

  const run = async (mode = filterMode, teams = customTeams, wc = wcTeams) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/mlb/betting-sim`, { params: buildParams(mode, teams, wc) });
      setData(data);
      if (teamFilter === "ALL") setTeamFilter(String(data.summaries[0]?.team_id || "ALL"));
    } catch (e) {
      setError("Simulation failed. Check backend.");
    } finally { setLoading(false); }
  };

  const runComparison = async () => {
    setCompareLoading(true);
    try {
      const [a, b, c, d] = await Promise.all([
        api.get(`/mlb/betting-sim`, { params: { starting_bankroll: bankroll, filter_mode: "all" } }),
        api.get(`/mlb/betting-sim`, { params: { starting_bankroll: bankroll, filter_mode: "above_500" } }),
        api.get(`/mlb/betting-sim`, { params: { starting_bankroll: bankroll, filter_mode: "custom", filter_teams: customTeams.join(",") } }),
        api.get(`/mlb/betting-sim`, { params: { starting_bankroll: bankroll, filter_mode: "custom", filter_teams: customTeams.join(","), wc_teams: wcTeams.join(",") } }),
      ]);
      setComparison({ baseline: a.data, above500: b.data, custom: c.data, custom_wc: d.data });
    } finally { setCompareLoading(false); }
  };

  useEffect(() => { run(); runComparison(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let base = [];
    if (teamFilter === "ALL") {
      Object.entries(data.team_rows).forEach(([tid, list]) => {
        const meta = data.team_meta[tid];
        list.forEach((r) => base.push({ ...r, team: meta.name, team_id: Number(tid), team_abbr: meta.abbr }));
      });
    } else {
      const list = data.team_rows[teamFilter] || [];
      const meta = data.team_meta[teamFilter];
      base = list.map((r) => ({ ...r, team: meta.name, team_id: Number(teamFilter), team_abbr: meta.abbr }));
    }
    if (dateFrom) base = base.filter((r) => r.date >= dateFrom);
    if (dateTo) base = base.filter((r) => r.date <= dateTo);
    if (onlyBets) base = base.filter((r) => r.bet_triggered === "Y");
    base.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    return base;
  }, [data, teamFilter, dateFrom, dateTo, onlyBets]);

  const dailyBets = useMemo(() => {
    if (!data) return [];
    let b = data.daily_bets;
    if (teamFilter !== "ALL") b = b.filter((x) => String(x.team_id) === teamFilter);
    if (dateFrom) b = b.filter((x) => x.date >= dateFrom);
    if (dateTo) b = b.filter((x) => x.date <= dateTo);
    return b;
  }, [data, teamFilter, dateFrom, dateTo]);

  const dailyBetsByDate = useMemo(() => {
    const m = {};
    dailyBets.forEach((b) => { (m[b.date] = m[b.date] || []).push(b); });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [dailyBets]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-2 flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Strategy Engine</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none">BETTING LAB</h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-3xl">
            Fade isolated 1-game losses at <span className="text-white">-170</span>. Bet on the team to win their next game when the count of in-cycle iso-1L hits the
            <span className="text-white font-mono-d"> 2nd / 3rd / 5th / 7th / 8th</span> trigger. Skip 1st, 4th, 6th, 9th+. Skip if both teams qualify same day.
          </p>
        </div>
      </div>

      {/* NEW drill-down flow: league → analyst → per-team table → A/B/C $ → optional side-by-side compare. */}
      <div className="mt-2">
        <StrategyMatrixSection
          mlbActiveTeams={activeTeams}
        />
      </div>

      {/* Live today's locked slate — auto-updates when admin shortlists */}
      <TodaysSlatePanel />

      {/* Legacy MLB-only single-strategy simulator below */}
      <div className="mt-12 pt-10 border-t border-white/10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Legacy single-strategy simulator</div>
        <p className="text-xs text-zinc-500 max-w-3xl">Original sandbox: pick one filter mode + bankroll and replay the season. Useful for one-off what-if runs.</p>
      </div>

      {/* Strategy rules card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 mt-6">
        <div className="bg-[#121212] border border-white/10 rounded-sm p-4 text-xs font-mono-d text-zinc-400">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Weekly Tiers (unit-to-win)</div>
          <div>weekly P/L &lt; $800 → <span className="text-white">$250</span> unit</div>
          <div>$800 ≤ P/L &lt; $1200 → <span className="text-white">$150</span> unit</div>
          <div>$1200 ≤ P/L &lt; $1500 → <span className="text-white">$75</span> unit</div>
          <div className="text-[#FF3B30]">P/L ≤ −$750 → STOP for week</div>
          <div className="text-[#22C55E]">P/L ≥ $1500 → take profit, STOP for week</div>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-sm p-4 text-xs font-mono-d text-zinc-400">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Odds Math (-170)</div>
          <div>Unit $250 → stake <span className="text-white">$425.00</span>, win <span className="text-[#22C55E]">+$250</span>, loss <span className="text-[#FF3B30]">−$425</span></div>
          <div>Unit $150 → stake <span className="text-white">$255.00</span>, win <span className="text-[#22C55E]">+$150</span>, loss <span className="text-[#FF3B30]">−$255</span></div>
          <div>Unit $75 → stake <span className="text-white">$127.50</span>, win <span className="text-[#22C55E]">+$75</span>, loss <span className="text-[#FF3B30]">−$127.50</span></div>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-sm p-4 flex items-end gap-2">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Starting Bankroll</div>
            <input
              data-testid="betting-bankroll-input"
              type="number" value={bankroll} onChange={(e) => setBankroll(Number(e.target.value))}
              className="w-full bg-[#0A0A0A] border border-white/15 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2 font-mono-d text-white"
            />
          </div>
          <button
            onClick={() => { run(); runComparison(); }} disabled={loading} data-testid="betting-simulate-button"
            className="flex items-center gap-2 px-5 py-2 bg-[#007AFF] hover:bg-[#0064D6] disabled:opacity-50 text-white rounded-sm text-xs uppercase tracking-wider font-bold hover:-translate-y-0.5 transition-all"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Simulate
          </button>
        </div>
      </div>

      {/* Strategy Comparison Panel */}
      <ComparisonPanel
        comparison={comparison}
        loading={compareLoading}
        customTeams={customTeams}
        setCustomTeams={setCustomTeams}
        wcTeams={wcTeams}
        setWcTeams={setWcTeams}
        bankroll={bankroll}
        teams={data?.summaries || []}
        onRerun={runComparison}
      />

      {/* Strategy tab selector */}
      <div className="flex items-center gap-2 mb-6 mt-8 flex-wrap" data-testid="strategy-tabs">
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mr-3">View Strategy:</span>
        {[
          { id: "all", label: "A · Baseline", sub: "All teams" },
          { id: "above_500", label: "B · Above .500", sub: "On bet day" },
          { id: "custom", label: "C · Chop Trend", sub: `${customTeams.length} iso-1L teams` },
          { id: "custom_wc", label: "D · Win-Continuation Trend", sub: `Chop + ${wcTeams.length} WC teams` },
        ].map((s) => (
          <button
            key={s.id}
            onClick={() => { setFilterMode(s.id); run(s.id, customTeams, wcTeams); }}
            data-testid={`strategy-tab-${s.id}`}
            className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wider font-bold border transition-colors ${
              filterMode === s.id ? "bg-[#007AFF] border-[#007AFF] text-white" : "bg-transparent border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
            }`}
          >
            {s.label}
            <div className="text-[9px] font-normal tracking-normal opacity-70">{s.sub}</div>
          </button>
        ))}
      </div>

      {error && <div className="text-[#FF3B30] mb-4">{error}</div>}

      {!data ? (
        <div className="text-zinc-500 font-mono-d" data-testid="betting-loading">Running simulation…</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            <Kpi label="Final Bankroll" value={money(data.final_bankroll)}
              accent={data.net_profit > 0 ? "text-[#22C55E]" : data.net_profit < 0 ? "text-[#FF3B30]" : "text-white"}
              sub={`Start ${money(data.starting_bankroll)}`} />
            <Kpi label="Net P/L" value={money(data.net_profit)}
              accent={data.net_profit > 0 ? "text-[#22C55E]" : "text-[#FF3B30]"} />
            <Kpi label="Qualifications" value={data.total_qualifications}
              sub="Every isolated 1-game L" />
            <Kpi label="Triggers" value={data.total_triggers}
              sub="At 2nd/3rd/5th/7th/8th" />
            <Kpi label="Bets Placed" value={data.total_bets}
              sub={`${data.total_wins}W / ${data.total_losses}L · ${pct(data.overall_win_rate)}`}
              accent={data.overall_win_rate >= 0.63 ? "text-[#22C55E]" : "text-[#FF3B30]"} />
            <Kpi label="Skip · Weekly" value={data.total_skipped_weekly_cap}
              sub="Stop-loss / take-profit" />
            <Kpi label="Skip · Both Teams" value={data.total_skipped_both_teams}
              sub="Matchup conflicts" />
          </div>

          {/* Funnel explainer */}
          <div className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-8" data-testid="funnel-explainer">
            <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Strategy Funnel</div>
            <div className="flex flex-col md:flex-row items-stretch gap-3 font-mono-d text-sm">
              <div className="flex-1 bg-[#0A0A0A] border border-white/10 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Step 1 · Qualifications</div>
                <div className="font-display text-3xl text-white mt-1">{data.total_qualifications}</div>
                <div className="text-xs text-zinc-500 mt-1">Total isolated 1-game L's league-wide</div>
              </div>
              <div className="flex items-center justify-center text-zinc-600 font-display text-2xl">→</div>
              <div className="flex-1 bg-[#0A0A0A] border border-white/10 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Step 2 · Triggers</div>
                <div className="font-display text-3xl text-[#007AFF] mt-1">{data.total_triggers}</div>
                <div className="text-xs text-zinc-500 mt-1">Hit 2nd/3rd/5th/7th/8th in cycle</div>
              </div>
              <div className="flex items-center justify-center text-zinc-600 font-display text-2xl">→</div>
              <div className="flex-1 bg-[#0A0A0A] border border-white/10 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Step 3 · Bets Placed</div>
                <div className="font-display text-3xl text-white mt-1">{data.total_bets}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  After {data.total_skipped_weekly_cap} weekly-skips + {data.total_skipped_both_teams} both-team-skips
                </div>
              </div>
            </div>
          </div>

          {/* Leaderboards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
            <Leaderboard title="TOP 10 BY QUALIFICATIONS" rows={data.top10_qualifications} variant="qualifications" testId="top10-qualifications" />
            <Leaderboard title="TOP 10 BY NET PROFIT" rows={data.top10_profit} variant="profit" testId="top10-profit" />
            <Leaderboard title="TOP 10 BY BETS PLACED" rows={data.top10_volume} variant="volume" testId="top10-volume" />
          </div>

          {/* Filters */}
          <div className="bg-[#121212] border border-white/10 rounded-sm p-4 mb-6 flex items-center gap-4 flex-wrap">
            <Funnel size={16} className="text-zinc-500" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Team</div>
              <select
                data-testid="filter-team"
                value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-1.5 text-sm font-mono-d"
              >
                <option value="ALL">All Teams</option>
                {data.summaries.map((s) => <option key={s.team_id} value={String(s.team_id)}>{s.team}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">From</div>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from"
                className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-1.5 text-sm font-mono-d" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">To</div>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to"
                className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-1.5 text-sm font-mono-d" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 mt-4">
              <input type="checkbox" checked={onlyBets} onChange={(e) => setOnlyBets(e.target.checked)} data-testid="filter-only-bets" />
              Only show rows with bets
            </label>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => csvDownload(`game_log_${teamFilter}.csv`, rows)} data-testid="csv-gamelog-button"
                className="flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-wider rounded-sm">
                <Download size={14} /> CSV Game Log
              </button>
              <button
                onClick={() => csvDownload(`daily_bets_${teamFilter}.csv`, dailyBets)} data-testid="csv-bets-button"
                className="flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-wider rounded-sm">
                <Download size={14} /> CSV Bets
              </button>
            </div>
          </div>

          {/* Per-team funnel (only when a team is selected) */}
          {teamFilter !== "ALL" && (() => {
            const ts = data.summaries.find((s) => String(s.team_id) === teamFilter);
            if (!ts) return null;
            return (
              <div className="bg-[#121212] border border-[#007AFF]/40 rounded-sm p-5 mb-6" data-testid="team-funnel">
                <div className="flex items-baseline justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF]">Team Funnel</div>
                    <div className="font-display text-3xl">{ts.team}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Net P/L</div>
                    <div className={`font-display text-3xl ${ts.net_profit > 0 ? "text-[#22C55E]" : ts.net_profit < 0 ? "text-[#FF3B30]" : "text-zinc-400"}`}>
                      {money(ts.net_profit)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 font-mono-d text-xs">
                  <FunnelStat label="Qualifications" value={ts.qualifications} hint="Every iso-1L" />
                  <FunnelStat label="Triggers" value={ts.triggers} hint="At 2/3/5/7/8" accent="text-[#007AFF]" />
                  <FunnelStat label="Bets Placed" value={ts.bets} hint={`${ts.wins}W / ${ts.losses}L`} />
                  <FunnelStat label="Skip · Weekly" value={ts.skipped_weekly} hint="Stop-loss paused" accent="text-yellow-400" />
                  <FunnelStat label="Skip · Both" value={ts.skipped_both} hint="Matchup conflict" accent="text-yellow-400" />
                </div>
                <div className="mt-3 text-xs text-zinc-500 font-mono-d">
                  Loss cycles: {ts.cycle_loss_count} · Win cycles: {ts.cycle_win_count} · Current SoS-1L: {ts.sos_iso_1L_current} · Current SoS-multi-L: {ts.sos_multi_L_current}
                </div>
              </div>
            );
          })()}

          {/* Daily bets feed */}
          <div className="bg-[#121212] border border-white/10 rounded-sm mb-10">
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
              <ChartLine size={18} className="text-[#007AFF]" />
              <h2 className="font-display text-2xl tracking-wider">DAILY BETS FEED</h2>
              <span className="ml-3 text-[10px] uppercase tracking-widest text-zinc-500">{dailyBets.length} entries</span>
            </div>
            <div className="max-h-[480px] overflow-y-auto" data-testid="daily-bets-feed">
              {dailyBetsByDate.length === 0 && <div className="p-6 text-zinc-500 text-sm">No bets in current filter.</div>}
              {dailyBetsByDate.map(([date, items]) => (
                <div key={date} className="border-b border-white/5">
                  <div className="px-5 py-2 bg-black/40 text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-mono-d sticky top-0">{date}</div>
                  {items.map((b, i) => (
                    <motion.div
                      key={`${b.gamePk}-${b.team_id}-${i}`}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="px-5 py-3 flex items-center gap-4 hover:bg-white/5"
                      data-testid="daily-bet-row"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-head font-bold">
                          <span className={b.result === "W" ? "text-[#22C55E]" : b.result === "L" ? "text-[#FF3B30]" : "text-zinc-400"}>
                            {b.team}
                          </span>
                          <span className="text-zinc-600 mx-2">vs</span>
                          <span className="text-zinc-300">{b.opponent}</span>
                        </div>
                        <div className="text-[11px] font-mono-d text-zinc-500 truncate">
                          {b.reason}
                          {b.skipped_reason ? ` · ${b.skipped_reason}` : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        {b.result === "SKIP-BOTH" || b.result === "SKIP-WEEK" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 border border-yellow-500/40 text-yellow-400 text-[10px] uppercase tracking-widest rounded-sm">
                            <Warning size={12} /> {b.result}
                          </span>
                        ) : (
                          <>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500">unit ${b.unit} · stake ${b.stake}</div>
                            <div className={`font-display text-2xl leading-none mt-1 ${b.profit > 0 ? "text-[#22C55E]" : "text-[#FF3B30]"}`}>
                              {money(b.profit)}
                            </div>
                            <div className="text-[10px] font-mono-d text-zinc-500">bank {money(b.bankroll_after)}</div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Sequence View — season W/L with bets marked */}
          <SequenceView
            data={data}
            filterMode={filterMode}
            customTeams={customTeams}
            wcTeams={wcTeams}
          />

          {/* Daily Bets Table with explanations */}
          <DailyBetsTable
            data={data}
            filterMode={filterMode}
            dailyBets={dailyBets}
          />

          {/* Per-game table */}
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10">
              <h2 className="font-display text-2xl tracking-wider">GAME LOG</h2>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                {rows.length} rows · {teamFilter === "ALL" ? "All teams" : data.team_meta[teamFilter]?.name}
              </div>
            </div>
            <div className="overflow-x-auto max-h-[640px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-black/90 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    {teamFilter === "ALL" && <th className="text-left px-2 py-2">Team</th>}
                    <th className="text-left px-2 py-2">Opp</th>
                    <th className="text-center px-2 py-2">R</th>
                    <th className="text-right px-2 py-2">Score</th>
                    <th className="text-center px-2 py-2">Strk</th>
                    <th className="text-right px-2 py-2">Cyc#</th>
                    <th className="text-right px-2 py-2">iso-1L</th>
                    <th className="text-right px-2 py-2">SoS-1L</th>
                    <th className="text-right px-2 py-2">SoS-ML</th>
                    <th className="text-center px-2 py-2">Bet?</th>
                    <th className="text-right px-2 py-2">Stake</th>
                    <th className="text-center px-2 py-2">Out</th>
                    <th className="text-right px-2 py-2">P/L</th>
                    <th className="text-right px-2 py-2">Unit</th>
                    <th className="text-right px-2 py-2">Bank</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 1200).map((r, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5" data-testid="gamelog-row">
                      <td className="px-3 py-1.5 font-mono-d text-zinc-300">{r.date}</td>
                      {teamFilter === "ALL" && <td className="px-2 py-1.5 font-head text-xs">{r.team_abbr}</td>}
                      <td className="px-2 py-1.5 text-zinc-400 text-xs truncate max-w-[140px]">{r.opponent}</td>
                      <td className={`px-2 py-1.5 text-center font-bold ${r.result === "W" ? "text-[#22C55E]" : "text-[#FF3B30]"}`}>{r.result}</td>
                      <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-300">{r.score}</td>
                      <td className="px-2 py-1.5 text-center font-mono-d text-xs">
                        <span className={r.streak_type === "W" ? "text-[#007AFF]" : "text-[#FF3B30]"}>{r.streak_type}{r.streak_len}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-400">{r.cycle_loss}</td>
                      <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-400">{r.in_cycle_iso_1L}</td>
                      <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-400">{r.sos_iso_1L}</td>
                      <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-400">{r.sos_multi_L}</td>
                      <td className="px-2 py-1.5 text-center">
                        {r.bet_triggered === "Y" ? (
                          <span className="inline-block px-1.5 py-0.5 bg-[#007AFF] text-white text-[10px] font-bold rounded-sm">Y</span>
                        ) : <span className="text-zinc-600 text-xs">–</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-300">{r.bet_size != null ? `$${r.bet_size}` : "–"}</td>
                      <td className={`px-2 py-1.5 text-center text-xs font-bold ${
                        r.bet_outcome === "W" ? "text-[#22C55E]" :
                        r.bet_outcome === "L" ? "text-[#FF3B30]" :
                        r.bet_outcome ? "text-yellow-400" : "text-zinc-600"
                      }`}>{r.bet_outcome || "–"}</td>
                      <td className={`px-2 py-1.5 text-right font-mono-d text-xs ${r.profit > 0 ? "text-[#22C55E]" : r.profit < 0 ? "text-[#FF3B30]" : "text-zinc-500"}`}>
                        {r.profit !== 0 ? money(r.profit) : "–"}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-400">{r.progression_unit != null ? `$${r.progression_unit}` : "–"}</td>
                      <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-300">${r.bankroll}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 1200 && (
                <div className="p-3 text-center text-xs text-zinc-500 border-t border-white/10">
                  Showing first 1200 rows of {rows.length}. Download CSV for the rest.
                </div>
              )}
            </div>
          </div>

          {/* Weekly log */}
          <div className="bg-[#121212] border border-white/10 rounded-sm mt-10">
            <div className="px-5 py-4 border-b border-white/10">
              <h2 className="font-display text-2xl tracking-wider">WEEKLY P/L LOG</h2>
            </div>
            <div className="overflow-x-auto max-h-[300px]">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-2">Week of</th>
                    <th className="text-right px-4 py-2">Bets</th>
                    <th className="text-right px-4 py-2">Weekly P/L</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.weekly_log.map((w, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="px-4 py-1.5 font-mono-d text-zinc-300">{w.week_start}</td>
                      <td className="px-4 py-1.5 text-right font-mono-d text-zinc-400">{w.bets}</td>
                      <td className={`px-4 py-1.5 text-right font-mono-d ${w.pnl > 0 ? "text-[#22C55E]" : w.pnl < 0 ? "text-[#FF3B30]" : "text-zinc-400"}`}>
                        {money(w.pnl)}
                      </td>
                      <td className="px-4 py-1.5 text-[10px] uppercase tracking-wider">
                        {w.pnl <= -750 ? <span className="text-[#FF3B30] flex items-center gap-1"><TrendDown size={12} /> Stop-loss</span> :
                         w.pnl >= 1500 ? <span className="text-[#22C55E] flex items-center gap-1"><TrendUp size={12} /> Take-profit</span> :
                         <span className="text-zinc-500">Active</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FunnelStat({ label, value, hint, accent }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-3xl mt-1 ${accent || "text-white"}`}>{value}</div>
      <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>
    </div>
  );
}

function ComparisonPanel({ comparison, loading, customTeams, setCustomTeams, wcTeams, setWcTeams, teams, onRerun }) {
  const [showPicker, setShowPicker] = React.useState(false);
  const [showWcPicker, setShowWcPicker] = React.useState(false);
  const cols = comparison ? [
    { key: "baseline", title: "A · BASELINE", sub: "All teams", data: comparison.baseline, accent: "text-zinc-400" },
    { key: "above500", title: "B · ABOVE .500", sub: "On bet day", data: comparison.above500, accent: "text-[#007AFF]" },
    { key: "custom", title: "C · CHOP TREND", sub: `${customTeams.length} iso-1L`, data: comparison.custom, accent: "text-[#22C55E]" },
    { key: "custom_wc", title: "D · WIN-CONT", sub: `Chop + ${wcTeams.length} WC`, data: comparison.custom_wc, accent: "text-yellow-400" },
  ] : [];

  const m = (n) => n == null ? "—" : (n > 0 ? "+" : n < 0 ? "−" : "") + "$" + Math.abs(Number(n)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="bg-[#121212] border border-[#007AFF]/30 rounded-sm p-5 mb-8" data-testid="comparison-panel">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF]">Strategy Comparison</div>
          <h2 className="font-display text-3xl">A · B · C · D — HEAD-TO-HEAD</h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            A=baseline · B=above-.500 filter · <span className="text-[#22C55E]">C=Chop Trend</span> (fade iso-1L on your custom list) · <span className="text-yellow-400">D=Win-Continuation Trend</span> (Chop + W2 bets after a loss streak breaks).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowPicker(!showPicker)} data-testid="custom-teams-toggle"
            className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10 rounded-sm">
            iso-1L Teams ({customTeams.length})
          </button>
          <button onClick={() => setShowWcPicker(!showWcPicker)} data-testid="wc-teams-toggle"
            className="px-3 py-1.5 text-xs uppercase tracking-wider border border-yellow-400/40 text-yellow-400 hover:bg-yellow-400/10 rounded-sm">
            WC Teams ({wcTeams.length})
          </button>
          <button onClick={onRerun} disabled={loading} data-testid="comparison-rerun"
            className="flex items-center gap-2 px-4 py-1.5 bg-[#007AFF] hover:bg-[#0064D6] disabled:opacity-50 text-white text-xs uppercase tracking-wider font-bold rounded-sm">
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Re-run
          </button>
        </div>
      </div>

      {showPicker && (
        <div className="bg-[#0A0A0A] border border-[#22C55E]/30 rounded-sm p-3 mb-4 max-h-48 overflow-y-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-1" data-testid="custom-teams-picker">
          <div className="col-span-full text-[10px] uppercase tracking-widest text-[#22C55E] mb-1">iso-1L teams (Strategy C & D)</div>
          {teams.map((t) => {
            const checked = customTeams.includes(t.team_id);
            return (
              <label key={t.team_id} className="flex items-center gap-2 px-2 py-1 hover:bg-white/5 cursor-pointer text-xs">
                <input type="checkbox" checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) setCustomTeams([...customTeams, t.team_id]);
                    else setCustomTeams(customTeams.filter((x) => x !== t.team_id));
                  }} />
                <span className="font-mono-d text-zinc-300">{t.team}</span>
              </label>
            );
          })}
        </div>
      )}

      {showWcPicker && (
        <div className="bg-[#0A0A0A] border border-yellow-400/30 rounded-sm p-3 mb-4 max-h-48 overflow-y-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-1" data-testid="wc-teams-picker">
          <div className="col-span-full text-[10px] uppercase tracking-widest text-yellow-400 mb-1">Win-Continuation teams (Strategy D only) — bet on next game after W breaks a loss streak</div>
          {teams.map((t) => {
            const checked = wcTeams.includes(t.team_id);
            return (
              <label key={t.team_id} className="flex items-center gap-2 px-2 py-1 hover:bg-white/5 cursor-pointer text-xs">
                <input type="checkbox" checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) setWcTeams([...wcTeams, t.team_id]);
                    else setWcTeams(wcTeams.filter((x) => x !== t.team_id));
                  }} />
                <span className="font-mono-d text-zinc-300">{t.team}</span>
              </label>
            );
          })}
        </div>
      )}

      {loading || !comparison ? (
        <div className="text-zinc-500 font-mono-d text-sm py-8 text-center">Computing 4 strategies in parallel…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="text-left py-2 px-2">Metric</th>
                {cols.map((c) => (
                  <th key={c.key} className="text-right px-3 py-2">
                    <div className={`font-display text-base ${c.accent}`}>{c.title}</div>
                    <div className="text-[10px] text-zinc-500 font-normal tracking-wider">{c.sub}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono-d">
              {[
                ["Total Bets", (d) => d.total_bets],
                ["  Wins/Losses", (d) => `${d.total_wins}-${d.total_losses}`],
                ["  Win Rate", (d) => (d.overall_win_rate * 100).toFixed(1) + "%", "win"],
                ["iso-1L bets", (d) => d.total_iso_bets || 0],
                ["iso-1L profit", (d) => m(d.total_iso_profit || 0), "iso"],
                ["WC bets", (d) => d.total_wc_bets || 0],
                ["WC profit", (d) => m(d.total_wc_profit || 0), "wc"],
                ["Skip · weekly", (d) => d.total_skipped_weekly_cap],
                ["Skip · filter", (d) => d.total_skipped_filter, "skip"],
                ["Net P/L", (d) => m(d.net_profit), "net", true],
                ["Final Bankroll", (d) => m(d.final_bankroll), null, true],
                ["ROI", (d) => ((d.net_profit / d.starting_bankroll) * 100).toFixed(1) + "%", "roi", true],
              ].map(([label, fn, accent, bold], i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-1.5 px-2 text-zinc-400 text-xs">{label}</td>
                  {cols.map((c) => {
                    const v = fn(c.data);
                    let cls = "text-zinc-300";
                    if (accent === "net" || accent === "roi") {
                      const n = c.data.net_profit;
                      cls = n > 0 ? "text-[#22C55E]" : n < 0 ? "text-[#FF3B30]" : "text-zinc-400";
                    } else if (accent === "iso") {
                      const n = c.data.total_iso_profit;
                      cls = n > 0 ? "text-[#22C55E]" : n < 0 ? "text-[#FF3B30]" : "text-zinc-500";
                    } else if (accent === "wc") {
                      const n = c.data.total_wc_profit;
                      cls = n > 0 ? "text-[#22C55E]" : n < 0 ? "text-[#FF3B30]" : "text-zinc-500";
                    } else if (accent === "win") {
                      cls = c.data.overall_win_rate >= 0.63 ? "text-[#22C55E]" : "text-[#FF3B30]";
                    } else if (accent === "skip") {
                      cls = "text-yellow-400";
                    }
                    return (
                      <td key={c.key} className={`text-right px-3 py-1.5 tabular-nums ${cls} ${bold ? "font-bold" : ""}`}>{v}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-600">
            iso-1L: {teams.filter((t) => customTeams.includes(t.team_id)).map((t) => t.team).join(" · ") || "(none)"}
            <br />
            WC: {teams.filter((t) => wcTeams.includes(t.team_id)).map((t) => t.team).join(" · ") || "(none)"}
          </div>
        </div>
      )}
    </div>
  );
}

function Leaderboard({ title, rows, variant, testId }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm" data-testid={testId}>
      <div className="px-5 py-3 border-b border-white/10 text-[11px] uppercase tracking-[0.3em] text-zinc-400">{title}</div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
          <tr className="border-b border-white/10">
            <th className="text-left px-3 py-2 w-6">#</th>
            <th className="text-left px-2 py-2">Team</th>
            <th className="text-right px-2 py-2">Qual</th>
            <th className="text-right px-2 py-2">Trig</th>
            <th className="text-right px-2 py-2">Bets</th>
            <th className="text-right px-2 py-2">W-L</th>
            <th className="text-right px-2 py-2">Net</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.team_id} className="border-b border-white/5">
              <td className="px-3 py-2 font-mono-d text-zinc-500">{i + 1}</td>
              <td className="px-2 py-2 font-head font-bold text-xs">{r.team}</td>
              <td className={`px-2 py-2 text-right font-mono-d ${variant === "qualifications" ? "text-white" : "text-zinc-400"}`}>{r.qualifications}</td>
              <td className="px-2 py-2 text-right font-mono-d text-xs text-[#007AFF]">{r.triggers}</td>
              <td className={`px-2 py-2 text-right font-mono-d ${variant === "volume" ? "text-white" : "text-zinc-400"}`}>{r.bets}</td>
              <td className="px-2 py-2 text-right font-mono-d text-xs text-zinc-400">{r.wins}-{r.losses}</td>
              <td className={`px-2 py-2 text-right font-mono-d text-xs ${r.net_profit > 0 ? "text-[#22C55E]" : r.net_profit < 0 ? "text-[#FF3B30]" : "text-zinc-400"}`}>
                {money(r.net_profit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ── Sequence View component ──────────────────────────────────────────────────
function SequenceView({ data, filterMode, customTeams, wcTeams }) {
  if (!data) return null;

  // Determine which teams to show based on strategy
  let teamSet = new Set();
  let strategyLabel = "All Teams";
  let strategyAccent = "text-zinc-300";
  if (filterMode === "custom") {
    teamSet = new Set(customTeams);
    strategyLabel = "Strategy C — Chop Trend";
    strategyAccent = "text-[#22C55E]";
  } else if (filterMode === "custom_wc") {
    teamSet = new Set([...customTeams, ...wcTeams]);
    strategyLabel = "Strategy D — Win-Continuation Trend";
    strategyAccent = "text-yellow-400";
  } else if (filterMode === "above_500") {
    strategyLabel = "Strategy B — Above .500 teams (showing top by activity)";
    // show top 6 by qualifications
    const top = [...data.summaries].sort((a, b) => b.bets - a.bets).slice(0, 6);
    teamSet = new Set(top.map((t) => t.team_id));
  } else {
    strategyLabel = "Strategy A — Baseline (top 6 by bet volume)";
    const top = [...data.summaries].sort((a, b) => b.bets - a.bets).slice(0, 6);
    teamSet = new Set(top.map((t) => t.team_id));
  }

  const teamsToShow = data.summaries.filter((s) => teamSet.has(s.team_id));

  // map gamePk -> bet_type for this strategy's bets
  const betTypeByGamePk = {};
  data.daily_bets.forEach((b) => {
    if (b.result === "W" || b.result === "L") betTypeByGamePk[`${b.team_id}-${b.gamePk}`] = b.bet_type;
  });

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden mb-10" data-testid="sequence-view">
      <div className="px-5 py-4 border-b border-white/10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Season Sequence View</div>
        <h2 className={`font-display text-2xl tracking-wider ${strategyAccent}`}>{strategyLabel.toUpperCase()}</h2>
        <div className="text-[11px] text-zinc-500 font-mono-d mt-2 flex items-center gap-4 flex-wrap">
          <span><span className="text-[#22C55E] font-bold">W</span> = Win · <span className="text-[#FF3B30] font-bold">L</span> = Loss</span>
          <span className="bg-[#22C55E] text-black px-1.5 rounded-sm font-bold">W</span>= bet placed &amp; won
          <span className="bg-[#FF3B30] text-black px-1.5 rounded-sm font-bold">L</span>= bet placed &amp; lost
          <span className="border border-yellow-400 text-yellow-400 px-1.5 rounded-sm font-bold">!</span>= bet attempted but skipped
        </div>
      </div>
      <div className="p-5 space-y-5 max-h-[600px] overflow-y-auto">
        {teamsToShow.map((t) => {
          const rows = data.team_rows[String(t.team_id)] || [];
          const placed = rows.filter((r) => r.bet_triggered === "Y" && (r.bet_outcome === "W" || r.bet_outcome === "L"));
          const won = placed.filter((r) => r.bet_outcome === "W").length;
          const lost = placed.filter((r) => r.bet_outcome === "L").length;
          const skips = rows.filter((r) => r.bet_triggered === "Y" && r.bet_outcome !== "W" && r.bet_outcome !== "L").length;
          const wr = placed.length ? Math.round((won / placed.length) * 100) : 0;
          return (
            <div key={t.team_id} data-testid={`sequence-team-${t.team_id}`}>
              <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                <span className="font-display text-xl">{t.team}</span>
                <span className="text-[10px] font-mono-d text-zinc-500 uppercase tracking-widest">
                  {rows.length} games · {placed.length} bets ({won}W-{lost}L · {wr}%) · {skips} skipped
                </span>
                <span className={`text-sm font-mono-d ${t.net_profit > 0 ? "text-[#22C55E]" : t.net_profit < 0 ? "text-[#FF3B30]" : "text-zinc-500"}`}>
                  {t.net_profit > 0 ? "+" : t.net_profit < 0 ? "−" : ""}${Math.abs(t.net_profit).toFixed(2)}
                </span>
              </div>
              <div className="font-mono-d text-sm leading-loose tracking-wide bg-[#0A0A0A] border border-white/5 p-3 rounded-sm">
                {rows.map((r, idx) => {
                  const won_bet = r.bet_outcome === "W";
                  const lost_bet = r.bet_outcome === "L";
                  const skipped = r.bet_triggered === "Y" && !won_bet && !lost_bet;
                  const bt = betTypeByGamePk[`${t.team_id}-${r.gamePk}`];
                  const tagBg = bt === "wc" ? "ring-2 ring-yellow-400" : "";
                  if (won_bet) return (
                    <span key={idx} className={`inline-block bg-[#22C55E] text-black font-bold px-1.5 py-0.5 mx-0.5 my-0.5 rounded-sm ${tagBg}`}
                      title={`Bet WON · ${r.reason} · ${r.date} · ${r.opponent}`}>W</span>
                  );
                  if (lost_bet) return (
                    <span key={idx} className={`inline-block bg-[#FF3B30] text-black font-bold px-1.5 py-0.5 mx-0.5 my-0.5 rounded-sm ${tagBg}`}
                      title={`Bet LOST · ${r.reason} · ${r.date} · ${r.opponent}`}>L</span>
                  );
                  if (skipped) return (
                    <span key={idx} className="inline-block border border-yellow-400 text-yellow-400 font-bold px-1 py-0.5 mx-0.5 my-0.5 rounded-sm"
                      title={`Bet attempted but skipped: ${r.skipped_reason}`}>{r.result}!</span>
                  );
                  return (
                    <span key={idx} className={`mx-0.5 ${r.result === "W" ? "text-[#22C55E]" : "text-[#FF3B30]"}`}>{r.result}</span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Daily Bets Table with explanations ───────────────────────────────────────
function DailyBetsTable({ data, filterMode, dailyBets }) {
  if (!data) return null;
  const m = (n) => (n > 0 ? "+" : n < 0 ? "−" : "") + "$" + Math.abs(Number(n || 0)).toFixed(2);

  const settled = dailyBets.filter((b) => b.result === "W" || b.result === "L");
  const skipped = dailyBets.filter((b) => b.result && b.result.startsWith("SKIP"));

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden mb-10" data-testid="daily-bets-table">
      <div className="px-5 py-4 border-b border-white/10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Bets Log</div>
        <h2 className="font-display text-2xl tracking-wider">DAILY BETS WITH EXPLANATIONS</h2>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
          {settled.length} placed · {skipped.length} skipped
        </div>
      </div>
      <div className="overflow-x-auto max-h-[500px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-black/90 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-2 py-2">Team</th>
              <th className="text-left px-2 py-2">Opponent</th>
              <th className="text-center px-2 py-2">Type</th>
              <th className="text-left px-2 py-2">Why we bet</th>
              <th className="text-right px-2 py-2">Unit</th>
              <th className="text-right px-2 py-2">Stake</th>
              <th className="text-center px-2 py-2">Outcome</th>
              <th className="text-right px-2 py-2">P/L</th>
              <th className="text-right px-3 py-2">Bankroll</th>
            </tr>
          </thead>
          <tbody>
            {dailyBets.map((b, i) => {
              const isPlaced = b.result === "W" || b.result === "L";
              const isWin = b.result === "W";
              const isSkip = !isPlaced;
              return (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5" data-testid="daily-bet-row">
                  <td className="px-3 py-1.5 font-mono-d text-zinc-300 text-xs">{b.date}</td>
                  <td className="px-2 py-1.5 font-head font-bold text-sm">{b.team}</td>
                  <td className="px-2 py-1.5 text-zinc-400 text-xs truncate max-w-[180px]">{b.opponent}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                      b.bet_type === "wc"
                        ? "bg-yellow-400/20 text-yellow-400 border border-yellow-400/40"
                        : "bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/40"
                    }`}>
                      {b.bet_type === "wc" ? "WC" : "iso-1L"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-zinc-400 text-xs italic">
                    {b.reason}
                    {b.skipped_reason ? <span className="text-yellow-400 not-italic"> · {b.skipped_reason}</span> : null}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-300">{b.unit ? `$${b.unit}` : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-300">{b.stake ? `$${b.stake.toFixed(2)}` : "—"}</td>
                  <td className="px-2 py-1.5 text-center">
                    {isPlaced ? (
                      <span className={`inline-block px-2 py-0.5 rounded-sm font-bold text-xs ${
                        isWin ? "bg-[#22C55E] text-black" : "bg-[#FF3B30] text-black"
                      }`}>{b.result}</span>
                    ) : (
                      <span className="text-[10px] uppercase text-yellow-400 px-1.5 py-0.5 border border-yellow-400/40 rounded-sm">{b.result}</span>
                    )}
                  </td>
                  <td className={`px-2 py-1.5 text-right font-mono-d text-xs ${
                    b.profit > 0 ? "text-[#22C55E]" : b.profit < 0 ? "text-[#FF3B30]" : "text-zinc-500"
                  }`}>{isSkip ? "—" : m(b.profit)}</td>
                  <td className="px-3 py-1.5 text-right font-mono-d text-xs text-zinc-300">${b.bankroll_after.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {dailyBets.length === 0 && <div className="p-6 text-zinc-500 text-sm">No bets in current strategy/filter.</div>}
      </div>
    </div>
  );
}
